import { useState, useEffect, useCallback, useRef } from 'react';

export type CaptureType = 'screen' | 'window' | 'browser' | 'unknown';

interface ScreenCaptureState {
  isScreenBeingCaptured: boolean;
  captureType: CaptureType | null;
  lastDetectedAt: Date | null;
}

/**
 * Hook to detect browser-based screen capture attempts.
 *
 * Detection methods:
 * 1. Override navigator.mediaDevices.getDisplayMedia to detect screen sharing
 * 2. Monitor display-capture permission state changes
 *
 * Limitations:
 * - Cannot detect OS-level recorders (OBS, etc.)
 * - Cannot detect hardware capture (HDMI grabbers)
 * - Cannot detect phone cameras pointed at screen
 * - Cannot detect browser extensions with elevated privileges
 */
export function useScreenCaptureDetection(): ScreenCaptureState {
  const [isScreenBeingCaptured, setIsScreenBeingCaptured] = useState(false);
  const [captureType, setCaptureType] = useState<CaptureType | null>(null);
  const [lastDetectedAt, setLastDetectedAt] = useState<Date | null>(null);

  // Track active capture streams
  const activeCaptureStreams = useRef<Set<MediaStream>>(new Set());

  // Original getDisplayMedia reference
  const originalGetDisplayMedia = useRef<typeof navigator.mediaDevices.getDisplayMedia | null>(null);

  const handleCaptureDetected = useCallback((type: CaptureType) => {
    console.warn(`Screen capture detected: ${type}`);
    setIsScreenBeingCaptured(true);
    setCaptureType(type);
    setLastDetectedAt(new Date());
  }, []);

  const handleCaptureStopped = useCallback(() => {
    console.log('Screen capture stopped');
    setIsScreenBeingCaptured(false);
    setCaptureType(null);
  }, []);

  // Monitor stream tracks to detect when capture stops
  const monitorStream = useCallback((stream: MediaStream, type: CaptureType) => {
    activeCaptureStreams.current.add(stream);
    handleCaptureDetected(type);

    // Listen for track ended events
    stream.getTracks().forEach(track => {
      track.addEventListener('ended', () => {
        activeCaptureStreams.current.delete(stream);
        if (activeCaptureStreams.current.size === 0) {
          handleCaptureStopped();
        }
      });
    });

    // Also check if stream becomes inactive
    const checkStreamActive = setInterval(() => {
      if (!stream.active) {
        activeCaptureStreams.current.delete(stream);
        clearInterval(checkStreamActive);
        if (activeCaptureStreams.current.size === 0) {
          handleCaptureStopped();
        }
      }
    }, 1000);

    return () => clearInterval(checkStreamActive);
  }, [handleCaptureDetected, handleCaptureStopped]);

  useEffect(() => {
    // Method 1: Override getDisplayMedia to intercept screen capture requests
    if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
      originalGetDisplayMedia.current = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);

      navigator.mediaDevices.getDisplayMedia = async function(constraints?: DisplayMediaStreamOptions): Promise<MediaStream> {
        console.log('getDisplayMedia called - screen capture initiated');

        // Call original method
        const stream = await originalGetDisplayMedia.current!(constraints);

        // Determine capture type from tracks
        const videoTrack = stream.getVideoTracks()[0];
        let detectedType: CaptureType = 'unknown';

        if (videoTrack) {
          const settings = videoTrack.getSettings();
          // displaySurface can be: 'monitor', 'window', 'browser'
          const displaySurface = (settings as { displaySurface?: string }).displaySurface;

          if (displaySurface === 'monitor') {
            detectedType = 'screen';
          } else if (displaySurface === 'window') {
            detectedType = 'window';
          } else if (displaySurface === 'browser') {
            detectedType = 'browser';
          }
        }

        // Monitor this stream
        monitorStream(stream, detectedType);

        return stream;
      };
    }

    // Method 2: Monitor display-capture permission state
    let permissionStatus: PermissionStatus | null = null;

    const checkPermission = async () => {
      try {
        // Note: 'display-capture' permission query is not supported in all browsers
        permissionStatus = await navigator.permissions.query({
          name: 'display-capture' as PermissionName
        });

        const handlePermissionChange = () => {
          if (permissionStatus?.state === 'granted') {
            // Permission granted - capture might be active
            // This is a weaker signal than getDisplayMedia override
            console.log('Display capture permission state: granted');
          }
        };

        permissionStatus.addEventListener('change', handlePermissionChange);

        return () => {
          permissionStatus?.removeEventListener('change', handlePermissionChange);
        };
      } catch (error) {
        // Permission query not supported - this is expected in many browsers
        console.debug('Display capture permission query not supported');
      }
    };

    const permissionCleanup = checkPermission();

    // Cleanup
    return () => {
      // Restore original getDisplayMedia
      if (originalGetDisplayMedia.current && navigator.mediaDevices) {
        navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia.current;
      }

      // Cleanup permission listener
      permissionCleanup.then(cleanup => cleanup?.());

      // Clear active streams
      activeCaptureStreams.current.clear();
    };
  }, [monitorStream]);

  return {
    isScreenBeingCaptured,
    captureType,
    lastDetectedAt
  };
}
